import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getBlogPost } from '../services/blogService';
import { BlogPost } from '../types/blog';
import { useSeoTags, SITE_ORIGIN } from '../hooks/useSeoTags';
import './BlogPage.css';

const BlogDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation('blog');
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    const fetchPost = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getBlogPost(slug);
        setPost(response.data);
      } catch (err) {
        setError(t('error.notFound'));
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug, t]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // SEO: only fully-formed once we have the post. While loading or on error
  // we still emit basic title/description so crawlers landing on a refresh
  // don't see the global static <title>. JSON-LD BlogPosting is only emitted
  // when we have real content — otherwise we'd publish bogus schema.
  const blogTitle = post?.title || t('loading');
  const blogDescription =
    post?.excerpt || post?.title || t('subtitle', { defaultValue: 'Loopy blog' });
  const seoTags = useSeoTags({
    title: blogTitle,
    description: blogDescription,
    canonical: slug ? `/blog/${slug}` : '/blog',
    ogType: 'article',
    ogImage: post?.coverImageUrl || undefined,
    noindex: !post,
    jsonLd: post
      ? {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.excerpt,
          image: post.coverImageUrl || undefined,
          datePublished: post.publishedAt || post.createdAt,
          dateModified: post.updatedAt,
          author: post.author
            ? {
                '@type': 'Person',
                name: post.author.fullName || post.author.username,
              }
            : undefined,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${SITE_ORIGIN}/blog/${post.slug}`,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Loopy',
            logo: {
              '@type': 'ImageObject',
              url: `${SITE_ORIGIN}/icons/loopy_512.png`,
            },
          },
          keywords: post.tags.join(', '),
        }
      : undefined,
  });

  if (loading) {
    return (
      <div className="blog-page">
        {seoTags}
        <div className="blog-detail-container">
          <div className="blog-loading">
            <div className="blog-spinner" />
            <p>{t('loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-page">
        {seoTags}
        <div className="blog-detail-container">
          <div className="blog-error">
            <p>{error || t('error.notFound')}</p>
            <Link to="/blog" className="blog-back-link">{t('backToList')}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page">
      {seoTags}
      {/* Cover Image */}
      {post.coverImageUrl && (
        <div className="blog-detail-cover">
          <img src={post.coverImageUrl} alt={post.title} />
        </div>
      )}

      <div className="blog-detail-container">
        <Link to="/blog" className="blog-back-link">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5m7-7l-7 7 7 7" />
          </svg>
          {t('backToList')}
        </Link>

        <article className="blog-article">
          <header className="blog-article-header">
            <h1>{post.title}</h1>
            <div className="blog-article-meta">
              {post.author && (
                <span className="blog-article-author">
                  {post.author.fullName || post.author.username}
                </span>
              )}
              <span className="blog-article-date">{formatDate(post.publishedAt)}</span>
              <span className="blog-article-views">
                {post.viewCount} {t('views')}
              </span>
            </div>
            {post.tags.length > 0 && (
              <div className="blog-article-tags">
                {post.tags.map(tag => (
                  <span key={tag} className="blog-tag">{tag}</span>
                ))}
              </div>
            )}
          </header>

          <div className="blog-article-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {post.content}
            </ReactMarkdown>
          </div>
        </article>
      </div>
    </div>
  );
};

export default BlogDetailPage;
